import { useState, useCallback } from 'react';
import { noop } from 'lodash';
import type { ResultOf } from '@graphql-typed-document-node/core';

import MuiLink from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import CardMedia from '@mui/material/CardMedia';
import AvatarGroup from '@mui/material/AvatarGroup';
import Collapse from '@mui/material/Collapse';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Menu from '@mui/material/Menu';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { css } from '@emotion/react';

import { invariant } from '@e-socna/utils';
import { IconEnum, getFragmentData, graphql } from '@e-socna/data-layer';
import { EMPTY_OBJECT } from '@e-socna/empty';
import type {
  AddressTypeId,
  FragmentType,
  Id,
  ProjectionId,
} from '@e-socna/data-layer';

import { iconToMuiIcon } from '../../../utils/icon-to-mui-icon.util';
import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { isFormEmpty } from '../../../forms/utils/is-form-empty.util';

import { MenuItem } from '../../Menu/MenuItem';
import { ExpandMoreCard } from '../../ExpandMoreCard';
import { useGoogleApi } from '../../Map/hooks/google/useGoogleApi.hook';
import { useActionMenu } from '../../Menu/hooks/useActionMenu.hook';
import { Avatar } from '../../Avatar';
import type { Option } from '../../Menu/MenuItem/types/option.type';

import {
  ADDRESS_CARD_SIZE,
  ADDRESS_CARD_SX,
  DEFAULT_STATIC_MAP_HEIGHT,
} from '../constants/card.constant';

export const AddressCardCmp_Address_Fragment = graphql(`
  fragment AddressCardCmp_Address_Fragment on Address {
    _id
    typeIds
    postalInfo {
      streetNumber
      route
      locality
      postalCode
      department
      region
      countryAlpha2
      building
      additional
      businessPostCode {
        postalCode
        distributorOfficeNumber
      }
      postOfficeBox
      inseeCode
    }
    locationInfo {
      coordinates
      projectionId
      altitude
    }
    cadastralInfo {
      departmentCode
      municipalityCode
      oldMunicipalityCode
      districtCode
      section
      number
      sheet
    }
  }
`);

export type AddressCardCmp_Address = ResultOf<
  typeof AddressCardCmp_Address_Fragment
>;

interface Props {
  readonly actionsEnabled?: boolean;
  readonly address: FragmentType<typeof AddressCardCmp_Address_Fragment>;
  readonly onStartRemove?: (id: Id) => void;
  readonly onStartUpdate?: (id: Id) => void;
}

export const AddressCard = ({
  actionsEnabled = false,
  address: pAddress,
  onStartRemove: handleStartRemove,
  onStartUpdate: handleStartUpdate,
}: Readonly<Props>) => {
  invariant(
    !actionsEnabled || !!(handleStartRemove && handleStartUpdate),
    'onStartRemove & onStartUpdate are required when actionsEnabled is true',
  );

  const address = getFragmentData(AddressCardCmp_Address_Fragment, pAddress);

  const { addressTypesIdMap, projectionsIdMap, projectionsCodeMap } =
    useReferencesContext();

  const {
    anchorEl,
    handleCloseActionMenu,
    handleOpenActionMenu,
    isActionMenuOpen,
    options,
  } = useActionMenu({
    id: address._id,
    onStartRemove: handleStartRemove,
    onStartUpdate: handleStartUpdate,
  });

  const [expanded, setExpanded] = useState(false);

  const handleExpandClick = useCallback(() => {
    setExpanded((prevExpanded) => !prevExpanded);
  }, []);

  const { postalInfo, cadastralInfo, locationInfo } = address;

  const { destinationUrl, staticMapPreviewUrl } = useGoogleApi({ address });

  const postalInfoMainContent =
    postalInfo && !isFormEmpty({ node: postalInfo }) ? (
      <Typography color="text.secondary" variant="body2">
        {[
          postalInfo.streetNumber,
          postalInfo.route,
          postalInfo.locality,
          postalInfo.postalCode,
          postalInfo.department,
          postalInfo.region,
          postalInfo.countryAlpha2,
        ].join(', ')}
      </Typography>
    ) : null;

  const postalInfoSecondaryContent =
    postalInfo &&
    (postalInfo.building ||
      postalInfo.additional ||
      postalInfo.businessPostCode ||
      postalInfo.postOfficeBox ||
      postalInfo.inseeCode) ? (
      <>
        <Typography gutterBottom variant="overline">
          Complément d&apos;adresse
        </Typography>
        {!!postalInfo.building && (
          <Typography gutterBottom variant="body2">
            Bâtiment : {postalInfo.building}
          </Typography>
        )}
        {!!postalInfo.additional && (
          <Typography gutterBottom variant="body2">
            Informations Supplémentaires : {postalInfo.additional}
          </Typography>
        )}
        {!!postalInfo.businessPostCode && (
          <Typography gutterBottom variant="body2">
            {postalInfo.businessPostCode.postalCode} {postalInfo.locality} CEDEX
            {!!postalInfo.businessPostCode.distributorOfficeNumber &&
              ` ${postalInfo.businessPostCode.distributorOfficeNumber}`}
          </Typography>
        )}
        {!!postalInfo.postOfficeBox && (
          <Typography gutterBottom variant="body2">
            Boîte Postale : {postalInfo.postOfficeBox}
          </Typography>
        )}
        {!!postalInfo.inseeCode && (
          <Typography gutterBottom variant="body2">
            Code INSEE Commune : {postalInfo.inseeCode}
          </Typography>
        )}
      </>
    ) : null;

  const cadastralInfoContent =
    cadastralInfo && !isFormEmpty({ node: cadastralInfo }) ? (
      <>
        <Typography gutterBottom variant="overline">
          Cadastre
        </Typography>
        <Typography gutterBottom variant="body2">
          Code INSEE Département : {cadastralInfo.departmentCode}
          <br />
          Code INSEE Commune : {cadastralInfo.municipalityCode}
          <br />
          Commune Absorbée : {cadastralInfo.oldMunicipalityCode}
          <br />
          Arrondissement : {cadastralInfo.districtCode}
          <br />
          Section : {cadastralInfo.section}
          <br />
          Numéro : {cadastralInfo.number}
          <br />
          Feuille : {cadastralInfo.sheet}
        </Typography>
      </>
    ) : null;

  const isLatLon =
    locationInfo?.projectionId === projectionsCodeMap.Epsg4326._id;

  const locationInfoContent =
    locationInfo &&
    !isFormEmpty({
      node: locationInfo,
      ignore: ['_id', '__typename', 'projectionId'],
    }) ? (
      <>
        <Typography gutterBottom variant="overline">
          Coordonnées
        </Typography>
        <Typography gutterBottom variant="body2">
          Altitude : {locationInfo.altitude}
          <br />
          {isLatLon ? (
            <>
              Latitude : {locationInfo.coordinates[1]}
              <br />
              Longitude : {locationInfo.coordinates[0]}
            </>
          ) : (
            <>Coordonnées : {locationInfo.coordinates.join(', ')}</>
          )}
          <br />
          Projection :{' '}
          {projectionsIdMap[locationInfo.projectionId as ProjectionId]?.label ??
            'Inconnue'}
        </Typography>
      </>
    ) : null;

  const postalinfoIsMainContent = !!postalInfoMainContent;
  const locationInfoIsMainContent = !postalInfoMainContent && !!locationInfo;
  const cadastralInfoIsMainContent =
    !postalinfoIsMainContent && !locationInfoIsMainContent && !!cadastralInfo;

  const mainContent = (
    <>
      {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion */}
      {!!postalinfoIsMainContent && postalInfoMainContent}
      {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion */}
      {!!locationInfoIsMainContent && locationInfoContent}
      {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion */}
      {!!cadastralInfoIsMainContent && cadastralInfoContent}
    </>
  );

  const secondaryContent = (
    <>
      {postalInfoSecondaryContent}
      {!locationInfoIsMainContent && locationInfoContent}
      {!cadastralInfoIsMainContent && cadastralInfoContent}
    </>
  );

  const hasSecondaryContent =
    !!postalInfoSecondaryContent ||
    (!locationInfoIsMainContent && !!locationInfoContent) ||
    (!cadastralInfoIsMainContent && !!cadastralInfoContent);

  return (
    <Grid size={ADDRESS_CARD_SIZE} sx={ADDRESS_CARD_SX}>
      <Card>
        <CardHeader
          avatar={
            <AvatarGroup>
              {address.typeIds.map((typeId) => {
                const type = addressTypesIdMap[typeId as AddressTypeId];

                const typeLabel = type?.label ?? 'Inconnu';

                return (
                  <Avatar
                    icon={iconToMuiIcon(type?.chip.icon ?? IconEnum.LocationOn)}
                    key={`${address._id}-${typeId}`}
                    label={typeLabel}
                  />
                );
              })}
            </AvatarGroup>
          }
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...(actionsEnabled
            ? {
                action: (
                  <>
                    <Tooltip title="Actions sur l'Adresse">
                      <IconButton
                        aria-controls={
                          isActionMenuOpen
                            ? `${address._id}-actions-menu`
                            : undefined
                        }
                        aria-expanded={isActionMenuOpen ? 'true' : undefined}
                        aria-haspopup="true"
                        aria-label="Actions sur l'adresse"
                        id={`${address._id}-actions-menu-button`}
                        onClick={handleOpenActionMenu}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </Tooltip>
                    <Menu
                      anchorEl={anchorEl}
                      id={`${address._id}-actions-menu`}
                      onClose={handleCloseActionMenu}
                      open={isActionMenuOpen}
                      slotProps={{
                        list: {
                          'aria-labelledby': `${address._id}-actions-menu-button`,
                        },
                      }}
                    >
                      {options.map((option: Option) => (
                        <MenuItem
                          key={option.key}
                          onCloseMenu={handleCloseActionMenu}
                          option={option}
                        />
                      ))}
                    </Menu>
                  </>
                ),
              }
            : EMPTY_OBJECT)}
          css={css`
            .MuiCardHeader-action {
              display: contents;
            }
          `}
        />
        <CardMedia
          alt="Aperçu de la carte"
          component="img"
          height={DEFAULT_STATIC_MAP_HEIGHT}
          image={staticMapPreviewUrl}
        />
        <CardContent>{mainContent}</CardContent>
        <CardActions disableSpacing>
          <Button
            component={MuiLink}
            disabled={!destinationUrl}
            endIcon={<OpenInNewIcon />}
            href={destinationUrl}
            rel="noopener noreferrer"
            size="medium"
            target="_blank"
            variant="outlined"
          >
            Navigation
          </Button>
          <ExpandMoreCard
            expanded={expanded}
            onExpandClick={hasSecondaryContent ? handleExpandClick : noop}
          />
        </CardActions>
        {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion */}
        {!!hasSecondaryContent && (
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <CardContent>{secondaryContent}</CardContent>
          </Collapse>
        )}
      </Card>
    </Grid>
  );
};
